import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import FilterCheckbox from "@/common/components/FilterCheckbox";
import ListFilters from "@/common/components/ListFilters";
import LoadingSpinner from "@/common/components/LoadingSpinner";
import Table from "@/common/components/Table";
import { DatePickerWithRange } from "@/common/components/ui/dateRangePicker";
import { billingBillStatus } from "@/common/constants/statusBills.constants";
import useDebounce from "@/common/hooks/useDebounce";
import { useFilterDate } from "@/common/hooks/useFilterDate";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { Mapper } from "@/common/services/mapper";
import { CompanyBranchData } from "@/common/types/companyBranches.type";
import { useClientBills } from "@/features/clientBills/hooks/useClientBills";
import { useClientBillsTotalizer } from "@/features/clientBills/hooks/useClientBillsTotalizer";
import { CompanyBillsTransformer } from "@/features/companies/services/companyBillsTransformer";
import { defaultColumns } from "@/features/privateCompanies/constants/companyBills.constants";
import { useAvailableCompanyBranches } from "@/features/privateCompanies/hooks/companyBranches/useAvailableCompanyBranches";
import { CompanyBillData } from "@/features/privateCompanies/types/companyBills.type";
import { CompanyMovementsTotalizerData } from "@/features/privateCompanies/types/companyMovementsTotalizer.type";
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import ResumeList from "../../Totals/components/ResumeList";
import TextField from "@/common/components/TextField";
import { SearchIcon } from "lucide-react";
import useUserPermissions from "@/common/hooks/useUserPermissions";

const List = () => {
  const { id: companyId } = useParams();

  const { filterParams, setFilterParams } = useFilterParams({
    status: billingBillStatus.find(({ value }) => value === 'pending_payment')
  });
  const { date, formattedDate } = useFilterDate(filterParams);
  const [search, setSearch] = useDebounce(filterParams.search || '');

  const [searchBranch, setSearchBranch] = useDebounce("");

  const {
    data: bills,
    meta,
    isPending: isPendingCompanyBills,
    isFetching: isFetchingCompanyBills,
    error: companyBillsError = {} as any,
    refetch: refetchCompanyBills,
  } = useClientBills<CompanyBillData[]>({
    filters: {
      clientId: companyId || '',
      search,
      page: filterParams?.page || 1,
      branchId: filterParams.branch?.value,
      status: filterParams.status?.value,
      startDate: formattedDate.from,
      endDate: formattedDate.to,
      showDescendants: filterParams.showDescendants || undefined,
      requiresAttention: filterParams.requiresAttention || undefined
    },
    mapper: Mapper.mapWithTransformer<CompanyBillData[], any[]>({
      transformer: CompanyBillsTransformer.tableTransformer(),
    }),
  });

  const { data: billsTotalizer } = useClientBillsTotalizer({
    filters: {
      clientId: companyId || '',
      branchId: filterParams.branch?.value,
      status: filterParams.status?.value,
      openedAt: formattedDate.from,
      closedAt: formattedDate.to,
      showDescendants: filterParams.showDescendants || undefined,
      requiresAttention: filterParams.requiresAttention || undefined
    },
    mapper: Mapper.default<CompanyMovementsTotalizerData>(),
  })

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const userHasPermissionToListClientBillsTotalizers =
    isUserPermissionsPending || hasUserPermission('Admin::Billing::Bills::ClientBillsTotalizer')


  const { data: companyBranchesOptions } = useAvailableCompanyBranches({
    companyId,
    filters: { search: searchBranch },
    mapper: Mapper.mapToOptions<CompanyBranchData>({ labelFieldName: "display_name", valueFieldName: "id" })
  });

  useEffect(() => {
    if (!filterParams.branch?.value) setFilterParams((prev) => ({ ...prev, showDescendants: null }))
  }, [filterParams.branch?.value])

  if (!companyId) {
    return null;
  }

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-192px)]"
      statusCode={companyBillsError?.status}
      refetchFunction={refetchCompanyBills}
    >
      <div className="flex flex-col gap-6">
        <TextField
          LeftIcon={<SearchIcon />}
          placeholder="Pesquisar"
          className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
          onChange={(e) => {
            setSearch((e.target as HTMLInputElement).value);
            setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }))
          }}
          value={filterParams.search}
        />
        <div className="flex items-center justify-between -mt-2">
          <div className="flex flex-wrap items-center gap-2 gap-y-4">
            <DatePickerWithRange date={date} setDate={(date) => setFilterParams((params) => ({ ...params, date }))} />

            <ListFilters
              filters={[
                {
                  className: "w-56",
                  onChange: (status) => setFilterParams((prev) => ({ ...prev, status })),
                  value: filterParams.status,
                  options: [{ label: "Todas", value: '' }, ...(billingBillStatus || [])],
                  placeholder: "Status",
                  dataTestid: "filter_status",
                },
                {
                  className: "w-56",
                  onChange: (branch) => setFilterParams((prev) => ({ ...prev, branch })),
                  value: filterParams.branch,
                  onInputChange: (value) => setSearchBranch(value),
                  options: [{ label: "Todas", value: '' }, ...(companyBranchesOptions || [])],
                  placeholder: "Filial",
                },
              ]}
            />

            <FilterCheckbox
              id="showDescendants"
              name="showDescendants"
              label="Mostrar descendentes"
              isChecked={!!filterParams.showDescendants}
              onChange={(showDescendants) =>
                setFilterParams((prev) => ({ ...prev, showDescendants: showDescendants ? true : null }))
              }
              isDisabled={!filterParams.branch?.value}
            />
            <FilterCheckbox
              id="requiresAttention"
              name="requiresAttention"
              label="Demandam atenção"
              isChecked={!!filterParams.requiresAttention}
              onChange={(requiresAttention) =>
                setFilterParams((prev) => ({ ...prev, requiresAttention: requiresAttention ? true : null }))
              }
            />
          </div>

          <LoadingSpinner isLoading={isFetchingCompanyBills} />
        </div>
        
        {userHasPermissionToListClientBillsTotalizers && (
          <ResumeList data={billsTotalizer} requiresAttention={filterParams.requiresAttention} />
        )}

        <Table
          className="max-h-[calc(100vh-392px)]"
          columns={defaultColumns}
          isLoadingData={isPendingCompanyBills}
          rows={bills}
          totalPages={meta.total_pages}
          totalCount={meta.total}
          hasPagination
          page={filterParams.page || 1}
          setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
        />
      </div>
    </ErrorMessageWrapper>
  );
};

export default List;
